import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material';
import Alert from '@material-ui/lab/Alert';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ExitBackfillService from '../../../services/ExitBackfillService';
import { getAppTimezone } from '../../../utils/timezone';
import { reportDatePickerSlotProps } from '../../../utils/reportDatePickerSlotProps';

dayjs.extend(utc);
dayjs.extend(timezone);

const getAdminIdFromCookie = () => {
    const cookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('Biodit='));
    if (!cookie) return null;
    const jwtFromCookie = cookie.split('=')[1];
    const decodedJwt = JSON.parse(atob(jwtFromCookie.split('.')[1]));
    return decodedJwt.id;
};

const ExitBackfill = () => {
    const { t, i18n } = useTranslation();
    const tz = getAppTimezone();
    const maxDate = dayjs().tz(tz).subtract(1, 'day').endOf('day');

    const [selectedDate, setSelectedDate] = useState(maxDate);
    const [count, setCount] = useState(0);
    const [seed, setSeed] = useState(Date.now());
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);

    const formattedDate = selectedDate
        ? dayjs(selectedDate).tz(tz).format('YYYY-MM-DD')
        : '';

    const loadPreview = async (nextSeed = seed, nextCount = count) => {
        if (!formattedDate) return;

        setLoading(true);
        setError('');
        try {
            const response = await ExitBackfillService.preview(
                formattedDate,
                nextCount > 0 ? nextCount : undefined,
                nextSeed
            );
            const data = response.data;
            setPreview(data);
            setSeed(data.seed);
            if (!nextCount && data.eligibleTotal > 0) {
                setCount(data.eligibleTotal);
            } else if (data.eligibleTotal === 0) {
                setCount(0);
            } else if (nextCount > data.eligibleTotal) {
                setCount(data.eligibleTotal);
            }
        } catch (e) {
            const message =
                (e.response && e.response.data && e.response.data.message) ||
                t('systemAdmin.exitBackfill.loadError');
            setError(message);
            setPreview(null);
        } finally {
            setLoading(false);
        }
    };

    const handleLoad = () => {
        const initialCount = count > 0 ? count : undefined;
        loadPreview(Date.now(), initialCount);
    };

    const handleCountChange = e => {
        const value = parseInt(e.target.value, 10);
        if (Number.isNaN(value) || value < 0) {
            setCount(0);
            return;
        }
        const max = preview ? preview.eligibleTotal : value;
        setCount(Math.min(value, max));
    };

    const handlePreviewSelection = () => {
        if (!preview || preview.eligibleTotal === 0) return;
        loadPreview(seed, count || preview.eligibleTotal);
    };

    const handleReshuffle = () => {
        if (!preview || preview.eligibleTotal === 0) return;
        loadPreview(Date.now(), count || preview.eligibleTotal);
    };

    const handleGenerate = async () => {
        setConfirmOpen(false);
        const adminId = getAdminIdFromCookie();
        if (!adminId) {
            setError(t('systemAdmin.exitBackfill.authError'));
            return;
        }

        setGenerating(true);
        setError('');
        try {
            const response = await ExitBackfillService.generate({
                date: formattedDate,
                count: count || preview.selectedCount,
                seed,
                adminId,
            });
            setSuccess(
                t('systemAdmin.exitBackfill.generateSuccess', {
                    count: response.data.created,
                    date: formattedDate,
                })
            );
            await loadPreview(seed, count);
        } catch (e) {
            const message =
                (e.response && e.response.data && e.response.data.message) ||
                t('systemAdmin.exitBackfill.generateError');
            setError(message);
        } finally {
            setGenerating(false);
        }
    };

    const formatTime = value => {
        if (!value) return '';
        return dayjs(value).tz(tz).format('DD.MM.YYYY HH:mm:ss');
    };

    const canGenerate =
        preview &&
        preview.selected &&
        preview.selected.length > 0 &&
        count > 0 &&
        !loading &&
        !generating;

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('systemAdmin.exitBackfill.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('systemAdmin.exitBackfill.description')}
            </Typography>

            <Box
                display="flex"
                flexWrap="wrap"
                gap={2}
                alignItems="center"
                mb={3}
            >
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={i18n.language}
                >
                    <MobileDatePicker
                        label={t('systemAdmin.exitBackfill.dateLabel')}
                        value={selectedDate}
                        onChange={value => setSelectedDate(value)}
                        maxDate={maxDate}
                        format="DD/MM/YYYY"
                        slotProps={reportDatePickerSlotProps}
                    />
                </LocalizationProvider>

                <Button
                    variant="contained"
                    onClick={handleLoad}
                    disabled={loading || !formattedDate}
                    sx={{ backgroundColor: '#003d4d', '&:hover': { backgroundColor: '#002a35' } }}
                >
                    {loading ? (
                        <CircularProgress size={22} color="inherit" />
                    ) : (
                        t('systemAdmin.exitBackfill.load')
                    )}
                </Button>
            </Box>

            {preview && (
                <Box mb={2}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        {t('systemAdmin.exitBackfill.eligibleCount', {
                            count: preview.eligibleTotal,
                        })}
                    </Typography>

                    {preview.eligibleTotal > 0 && (
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={2}
                            alignItems="center"
                            mb={2}
                        >
                            <TextField
                                type="number"
                                label={t('systemAdmin.exitBackfill.countLabel')}
                                value={count}
                                onChange={handleCountChange}
                                inputProps={{
                                    min: 1,
                                    max: preview.eligibleTotal,
                                }}
                                sx={{ width: 200 }}
                            />
                            <Button
                                variant="outlined"
                                onClick={handlePreviewSelection}
                                disabled={loading || count < 1}
                            >
                                {t('systemAdmin.exitBackfill.preview')}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleReshuffle}
                                disabled={loading || count < 1}
                            >
                                {t('systemAdmin.exitBackfill.reshuffle')}
                            </Button>
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={() => setConfirmOpen(true)}
                                disabled={!canGenerate}
                            >
                                {generating ? (
                                    <CircularProgress size={22} color="inherit" />
                                ) : (
                                    t('systemAdmin.exitBackfill.generate')
                                )}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {preview && preview.eligibleTotal === 0 && (
                <Alert severity="info">
                    {t('systemAdmin.exitBackfill.emptyState')}
                </Alert>
            )}

            {preview && preview.selected && preview.selected.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {t('systemAdmin.exitBackfill.columns.name')}
                                </TableCell>
                                <TableCell>
                                    {t('systemAdmin.exitBackfill.columns.sap')}
                                </TableCell>
                                <TableCell>
                                    {t('systemAdmin.exitBackfill.columns.enter')}
                                </TableCell>
                                <TableCell>
                                    {t('systemAdmin.exitBackfill.columns.exit')}
                                </TableCell>
                                <TableCell>
                                    {t('systemAdmin.exitBackfill.columns.location')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {preview.selected.map(row => (
                                <TableRow key={row.user_id}>
                                    <TableCell>{row.employee_name}</TableCell>
                                    <TableCell>{row.user_id}</TableCell>
                                    <TableCell>
                                        {formatTime(row.enter_time)}
                                    </TableCell>
                                    <TableCell>
                                        {formatTime(row.exit_time)}
                                    </TableCell>
                                    <TableCell>
                                        {row.location_name || row.location}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>
                    {t('systemAdmin.exitBackfill.confirmTitle')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('systemAdmin.exitBackfill.confirmMessage', {
                            count:
                                count ||
                                (preview && preview.selectedCount) ||
                                0,
                            date: formattedDate,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>
                        {t('systemAdmin.exitBackfill.cancel')}
                    </Button>
                    <Button onClick={handleGenerate} color="warning" autoFocus>
                        {t('systemAdmin.exitBackfill.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError('')}
            >
                <Alert severity="error" onClose={() => setError('')}>
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess('')}
            >
                <Alert severity="success" onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ExitBackfill;
